import type React from "react";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import type { reportingPeriod, team } from "@duress/shared";
import { fmtPct, fmtInt } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";
import { useSetTeamRosterExcluded, useTeamRoster } from "@/features/teams/useTeamRoster";
import { handleTabListArrowKeys } from "@/lib/tabListKeyboard";

type StoreMetrics = reportingPeriod.StoreMetrics;
type DecommissionedDevice = reportingPeriod.DecommissionedDevice;

type SubView = "completely-inactive" | "idle-devices" | "decommissioned" | "team-roster";

const SUB_VIEWS: { id: SubView; label: string }[] = [
  { id: "completely-inactive", label: "Completely Inactive Teams" },
  { id: "idle-devices", label: "Idle Devices at Active Teams" },
  { id: "decommissioned", label: "Decommissioned Devices" },
  { id: "team-roster", label: "Team Roster" },
];

// ── Cell renderers ────────────────────────────────────────────────────────────

const teamCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const pctCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtPct(typeof v === "number" ? v : Number(v))}</span>
);

// ── Section A — Idle Devices at Active Teams ─────────────────────────────────

type IdleRow = StoreMetrics & { active_devices: number };

const IDLE_COLUMNS: DataTableColumn<IdleRow>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "active_devices", label: "Active Devices", render: numCell },
  { key: "idle_devices", label: "Idle Devices", render: numCell },
  { key: "devices_total", label: "Total Fleet", render: numCell },
  { key: "fleet_idle_rate", label: "Idle Rate", render: pctCell },
];

function IdleDevicesSection({ stores }: { stores: StoreMetrics[] }): React.JSX.Element {
  const rows: IdleRow[] = stores
    .filter((s) => s.sessions > 0 && s.idle_devices > 0)
    .map((s) => ({ ...s, active_devices: s.devices_total - s.idle_devices }));

  const tiles: KPITile[] = [
    { label: "Teams Affected", value: fmtInt(rows.length) },
    {
      label: "Total Idle Devices",
      value: fmtInt(rows.reduce((sum, s) => sum + s.idle_devices, 0)),
    },
    {
      label: "Average Idle Rate",
      value: rows.length
        ? fmtPct(rows.reduce((sum, s) => sum + s.fleet_idle_rate, 0) / rows.length)
        : fmtPct(0),
    },
  ];

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No idle devices at otherwise-active teams this period.
      </p>
    );
  }

  return (
    <>
      <KPIGrid tiles={tiles} />
      <div className="mt-4">
        <DataTable<IdleRow>
          columns={IDLE_COLUMNS}
          data={rows}
          defaultSort={{ key: "fleet_idle_rate", dir: "desc" }}
          searchKeys={["store"]}
        />
      </div>
    </>
  );
}

// ── Section B — Completely Inactive Teams ────────────────────────────────────

const INACTIVE_COLUMNS: DataTableColumn<StoreMetrics>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "devices_total", label: "Devices Registered", render: numCell },
  { key: "devices_watch", label: "Watch", render: numCell },
  { key: "devices_bodyworn", label: "Bodyworn", render: numCell },
];

function CompletelyInactiveTeamsSection({ stores }: { stores: StoreMetrics[] }): React.JSX.Element {
  const rows = stores.filter((s) => s.sessions === 0);

  const tiles: KPITile[] = [
    { label: "Inactive Teams", value: fmtInt(rows.length) },
    {
      label: "Devices Registered",
      value: fmtInt(rows.reduce((sum, s) => sum + s.devices_total, 0)),
    },
  ];

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No completely inactive teams this period.</p>
    );
  }

  return (
    <>
      <KPIGrid tiles={tiles} />
      <div className="mt-4">
        <DataTable<StoreMetrics>
          columns={INACTIVE_COLUMNS}
          data={rows}
          defaultSort={{ key: "devices_total", dir: "desc" }}
          searchKeys={["store"]}
        />
      </div>
    </>
  );
}

// ── Section C — Decommissioned Devices ───────────────────────────────────────

const deviceIdCell = (v: unknown): React.ReactNode =>
  typeof v === "string" && v.length > 0 ? v : <span className="text-muted-foreground">—</span>;

const DECOMMISSIONED_COLUMNS: DataTableColumn<DecommissionedDevice>[] = [
  { key: "watchName", label: "Device Name", render: teamCell },
  { key: "deviceId", label: "Device ID", render: deviceIdCell },
  { key: "store", label: "Team", render: teamCell },
];

function DecommissionedDevicesSection({
  devices,
}: {
  devices: DecommissionedDevice[];
}): React.JSX.Element {
  if (devices.length === 0) {
    return <p className="text-sm text-muted-foreground">No decommissioned devices tracked.</p>;
  }

  return (
    <DataTable<DecommissionedDevice>
      columns={DECOMMISSIONED_COLUMNS}
      data={devices}
      defaultSort={{ key: "store", dir: "asc" }}
      searchKeys={["store", "watchName"]}
    />
  );
}

// ── Section D — Team Roster management ───────────────────────────────────────
//
// The full list of teams ever seen for this client — not scoped to the
// selected period. A team counts toward "Active Teams" totals from the
// month it's first seen onward, even in later months where it's absent from
// that month's export. Excluding a team here (spare-device rows, trial
// teams that were never real) removes it from every period's totals
// immediately, without needing to re-upload any CSV.

type RosterRow = team.TeamRosterEntry & { firstSeenLabel: string };

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function firstSeenLabel(entry: team.TeamRosterEntry): string {
  return `${MONTH_ABBR[entry.firstSeenMonth - 1] ?? entry.firstSeenMonth} ${entry.firstSeenYear}`;
}

function TeamRosterSection({ clientId }: { clientId: string }): React.JSX.Element {
  const { data, isLoading, error, refetch } = useTeamRoster(clientId);
  const setExcluded = useSetTeamRosterExcluded(clientId);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleToggle = async (row: RosterRow): Promise<void> => {
    setPendingId(row.id);
    try {
      await setExcluded.mutateAsync({ teamId: row.id, excluded: !row.excluded });
    } finally {
      setPendingId(null);
    }
  };

  const columns: DataTableColumn<RosterRow>[] = [
    { key: "teamName", label: "Team", render: teamCell },
    { key: "firstSeenLabel", label: "First Seen" },
    {
      key: "excluded",
      label: "Status",
      render: (v) =>
        v ? (
          <span className="text-xs font-head font-medium text-muted-foreground">Excluded</span>
        ) : (
          <span className="text-xs font-head font-medium text-emerald-600">On roster</span>
        ),
    },
    {
      key: "id",
      label: "",
      render: (_v, row) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pendingId === row.id}
          onClick={() => {
            void handleToggle(row);
          }}
        >
          {row.excluded ? "Include" : "Exclude"}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingSkeleton variant="table" rows={4} />;
  }
  if (error) {
    return <ErrorCard message="Couldn't load the team roster." onRetry={refetch} />;
  }

  const rows: RosterRow[] = (data?.items ?? []).map((entry) => ({
    ...entry,
    firstSeenLabel: firstSeenLabel(entry),
  }));

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No teams uploaded for this client yet.</p>;
  }

  return (
    <DataTable<RosterRow>
      columns={columns}
      data={rows}
      defaultSort={{ key: "teamName", dir: "asc" }}
      searchKeys={["teamName"]}
    />
  );
}

const SUB_VIEW_DESCRIPTIONS: Record<SubView, string> = {
  "completely-inactive": "Teams where no device recorded a single session this period.",
  "idle-devices": "Devices with zero sessions at teams where other devices did have sessions.",
  decommissioned:
    "Devices the export flags as decommissioned — whether retired without ever recording a session, or retired after real usage history. Excluded from this team's ongoing fleet and idle-rate counts from the period after they were flagged.",
  "team-roster":
    "Every team ever seen for this client. Exclude entries that were never real teams (spare devices, trial teams) — this updates every period's totals immediately.",
};

// ── Pure presentational component ────────────────────────────────────────────

export interface InactiveTeamsProps {
  data: reportingPeriod.DashboardData;
  clientId?: string;
}

export function InactiveTeams({ data, clientId }: InactiveTeamsProps): React.JSX.Element {
  const [subView, setSubView] = useState<SubView>("completely-inactive");
  const stores = data.methodology.stores;
  const decommissioned = data.methodology.decommissioned_devices;

  const hasAnything =
    stores.some((s) => s.sessions > 0 && s.idle_devices > 0) ||
    stores.some((s) => s.sessions === 0) ||
    decommissioned.length > 0;

  // Team Roster management isn't scoped to a period, so it's always reachable
  // once a client is known — the "all clear" full-page state (below) only
  // makes sense when there's genuinely nowhere else useful to go.
  const subViews = clientId ? SUB_VIEWS : SUB_VIEWS.filter((v) => v.id !== "team-roster");

  if (!hasAnything && !clientId) {
    return (
      <div data-inactive-teams-state="all-active">
        <EmptyState
          icon={CheckCircle2}
          body="No idle devices, inactive teams, or decommissioned devices this period."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Sub-view">
        {subViews.map((v) => {
          const active = v.id === subView;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              data-value={v.id}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => {
                setSubView(v.id);
              }}
              onKeyDown={(e) => { handleTabListArrowKeys(e, (id) => { setSubView(id as SubView); }); }}
              className={`px-3 py-1.5 rounded-full text-xs font-head font-medium border transition-colors
                focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
                ${
                  active
                    ? "bg-phoenix-orange text-white border-phoenix-orange"
                    : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-phoenix-orange"
                }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <div className="card-duress p-5" data-inactive-teams-section={subView}>
        <p className="text-xs font-body text-muted-foreground mb-4">
          {SUB_VIEW_DESCRIPTIONS[subView]}
        </p>
        {subView === "completely-inactive" && <CompletelyInactiveTeamsSection stores={stores} />}
        {subView === "idle-devices" && <IdleDevicesSection stores={stores} />}
        {subView === "decommissioned" && <DecommissionedDevicesSection devices={decommissioned} />}
        {subView === "team-roster" &&
          (clientId ? (
            <TeamRosterSection clientId={clientId} />
          ) : (
            <p className="text-sm text-muted-foreground">Team roster is unavailable here.</p>
          ))}
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function InactiveTeamsTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return <LoadingSkeleton variant="table" />;
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return <InactiveTeams data={data} clientId={clientId} />;
}
